/**
 * What is currently picked up, and where it may go.
 *
 * Kept out of the board component so that the rules of selection can be read in one
 * place: what a click does depends on whether something is already held, whether the cell
 * is a legal destination, and whether the piece is yours.
 */

import {
  type BowShot,
  describeBows,
  type Move,
  ownerOf,
  type PlayerIndex,
} from '@gok/engine';
import type { FastPosition } from '@gok/engine';

export type Selection =
  | { readonly kind: 'piece'; readonly cell: number }
  /** A pawn lifted from the reserve, not yet placed. */
  | { readonly kind: 'reserve' }
  | null;

export interface Targets {
  /** Destination cell to the move that lands there. */
  readonly moves: ReadonlyMap<number, Move>;
  /** Destinations that take an enemy piece, drawn differently. */
  readonly captures: ReadonlySet<number>;
  /** Live bows for the held piece, with the cells that make each one live. */
  readonly bows: readonly BowShot[];
}

const EMPTY: Targets = { moves: new Map(), captures: new Set(), bows: [] };

/**
 * Where the current selection can go.
 *
 * Two kings adjacent to the same empty cell each generate a spawn move that reaches an
 * identical position, so for the reserve we keep whichever comes first — the log records
 * which king acted, and either is a truthful answer.
 */
export function targetsFor(
  position: FastPosition,
  legalMoves: readonly Move[],
  selection: Selection,
): Targets {
  if (!selection) return EMPTY;

  const moves = new Map<number, Move>();
  const captures = new Set<number>();

  for (const move of legalMoves) {
    if (selection.kind === 'reserve') {
      if (move.type !== 'spawnPiece') continue;
      if (!moves.has(move.toIndex)) moves.set(move.toIndex, move);
      continue;
    }

    if (move.type !== 'movePiece' || move.fromIndex !== selection.cell) {
      continue;
    }
    moves.set(move.toIndex, move);
    if (position.cells[move.toIndex] !== 0) captures.add(move.toIndex);
  }

  return {
    moves,
    captures,
    bows: selection.kind === 'piece' ? describeBows(position, selection.cell) : [],
  };
}

/**
 * What a press on `cell` should do.
 *
 * Tap-to-select and drag-and-drop share this: a press either plays a move, picks
 * something up, swaps to a different piece of yours, or clears the selection. Having the
 * four cases in one function is what lets the board handle a finger and a mouse with the
 * same code rather than the two drifting apart.
 */
export type PressResult =
  | { readonly action: 'play'; readonly move: Move }
  | { readonly action: 'select'; readonly selection: Selection }
  | { readonly action: 'clear' };

export function press(
  cells: Int8Array,
  toMove: PlayerIndex,
  targets: Targets,
  selection: Selection,
  cell: number,
): PressResult {
  const move = targets.moves.get(cell);
  if (move) return { action: 'play', move };

  const piece = cells[cell];
  if (piece === 0 || ownerOf(piece) !== toMove) return { action: 'clear' };

  // Pressing another of your own pieces swaps to it rather than deselecting, which is
  // what people expect and saves a tap on a touchscreen. Pressing the held piece again
  // puts it down.
  if (selection?.kind === 'piece' && selection.cell === cell) return { action: 'clear' };
  return { action: 'select', selection: { kind: 'piece', cell } };
}
